import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  OpenInNew as OpenInNewIcon,
  Refresh as RefreshIcon,
  VideoLibrary as VideoLibraryIcon,
} from '@mui/icons-material';
import higgsfieldService, {
  GenerateVideoPayload,
  HiggsfieldVideo,
  HiggsfieldVideoStatus,
} from '../../services/marketing-higgsfield.service';
import { PageHeader, EmptyState } from '../../components/Marketing';

const STATUS_COLORS: Record<HiggsfieldVideoStatus, 'default' | 'info' | 'success' | 'error'> = {
  pending: 'default',
  processing: 'info',
  completed: 'success',
  failed: 'error',
};

const STATUS_LABELS: Record<HiggsfieldVideoStatus, string> = {
  pending: 'Pendente',
  processing: 'A gerar...',
  completed: 'Pronto',
  failed: 'Falhou',
};

const STYLES = [
  'cinematic',
  'anime',
  'photorealistic',
  'cartoon',
  'artistic',
  '3d',
];

const ASPECT_RATIOS = ['16:9', '9:16', '1:1', '4:5'];

const defaultForm: GenerateVideoPayload = {
  prompt: '',
  style: 'cinematic',
  aspect_ratio: '16:9',
  duration: 5,
};

export default function Higgsfield() {
  const [videos, setVideos] = useState<HiggsfieldVideo[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [form, setForm] = useState<GenerateVideoPayload>(defaultForm);
  const [saving, setSaving] = useState(false);
  const [filterStatus, setFilterStatus] = useState<HiggsfieldVideoStatus | ''>('');
  const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false, message: '', severity: 'success',
  });
  const pollingRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await higgsfieldService.listVideos({
        status: filterStatus || undefined,
        limit: 24,
      });
      setVideos(res.data);
      setTotal(res.total);
    } catch {
      setSnack({ open: true, message: 'Erro ao carregar vídeos', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [filterStatus]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh vídeos em processamento a cada 10s
  useEffect(() => {
    const processing = videos.filter(v => v.status === 'processing' || v.status === 'pending');
    if (processing.length === 0) {
      if (pollingRef.current) clearInterval(pollingRef.current);
      return;
    }
    pollingRef.current = setInterval(async () => {
      for (const v of processing) {
        try {
          const updated = await higgsfieldService.refreshVideoStatus(v.id);
          setVideos(prev => prev.map(x => x.id === updated.id ? updated : x));
        } catch { /* silent */ }
      }
    }, 10000);
    return () => { if (pollingRef.current) clearInterval(pollingRef.current); };
  }, [videos]);

  const handleGenerate = async () => {
    if (!form.prompt.trim()) return;
    setSaving(true);
    try {
      const video = await higgsfieldService.generateVideo(form);
      setVideos(prev => [video, ...prev]);
      setTotal(t => t + 1);
      setSnack({ open: true, message: 'Vídeo em geração! Será actualizado automaticamente.', severity: 'success' });
      setGenerateOpen(false);
      setForm(defaultForm);
    } catch (e: any) {
      setSnack({ open: true, message: e?.response?.data?.error || 'Erro ao submeter geração', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleRefresh = async (v: HiggsfieldVideo) => {
    try {
      const updated = await higgsfieldService.refreshVideoStatus(v.id);
      setVideos(prev => prev.map(x => x.id === updated.id ? updated : x));
    } catch {
      setSnack({ open: true, message: 'Erro ao actualizar status', severity: 'error' });
    }
  };

  const handleDelete = async (v: HiggsfieldVideo) => {
    if (!window.confirm('Remover este vídeo?')) return;
    try {
      await higgsfieldService.deleteVideo(v.id);
      setVideos(prev => prev.filter(x => x.id !== v.id));
      setTotal(t => t - 1);
      setSnack({ open: true, message: 'Vídeo removido', severity: 'success' });
    } catch {
      setSnack({ open: true, message: 'Erro ao remover vídeo', severity: 'error' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="Higgsfield AI"
        subtitle="Geração de vídeos de marketing com inteligência artificial"
        icon={<VideoLibraryIcon sx={{ color: '#7C3AED' }} />}
        actions={
          <Stack direction="row" spacing={1} alignItems="center">
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Filtrar status</InputLabel>
              <Select
                label="Filtrar status"
                value={filterStatus}
                onChange={e => setFilterStatus(e.target.value as HiggsfieldVideoStatus | '')}
              >
                <MenuItem value="">Todos</MenuItem>
                {(Object.keys(STATUS_LABELS) as HiggsfieldVideoStatus[]).map(s => (
                  <MenuItem key={s} value={s}>{STATUS_LABELS[s]}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Recarregar">
              <IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setGenerateOpen(true)}
              sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}>
              Gerar Vídeo
            </Button>
          </Stack>
        }
      />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {total} vídeo{total !== 1 ? 's' : ''} no total
      </Typography>

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
      ) : videos.length === 0 ? (
        <EmptyState
          title="Sem vídeos"
          description="Clique em 'Gerar Vídeo' para criar o primeiro vídeo de marketing com IA."
        />
      ) : (
        <Grid container spacing={2}>
          {videos.map(v => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={v.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                {v.thumbnail_url ? (
                  <CardMedia component="img" height="160" image={v.thumbnail_url} alt={v.prompt} />
                ) : (
                  <Box
                    sx={{
                      height: 160,
                      bgcolor: 'action.hover',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexDirection: 'column',
                      gap: 1,
                    }}
                  >
                    {(v.status === 'pending' || v.status === 'processing') ? (
                      <>
                        <CircularProgress size={28} sx={{ color: '#7C3AED' }} />
                        <Typography variant="caption" color="text.secondary">A gerar vídeo...</Typography>
                      </>
                    ) : (
                      <VideoLibraryIcon sx={{ fontSize: 40, color: '#D1D5DB' }} />
                    )}
                  </Box>
                )}
                <CardContent sx={{ flex: 1, py: 1 }}>
                  <Stack direction="row" spacing={1} alignItems="center" mb={0.5}>
                    <Chip
                      label={STATUS_LABELS[v.status]}
                      color={STATUS_COLORS[v.status]}
                      size="small"
                    />
                    {v.style && (
                      <Chip label={v.style} size="small" variant="outlined" />
                    )}
                    <Chip label={v.aspect_ratio} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="body2" sx={{
                    mt: 0.5,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}>
                    {v.prompt}
                  </Typography>
                  {v.error_message && (
                    <Typography variant="caption" color="error" display="block" mt={0.5}>
                      {v.error_message}
                    </Typography>
                  )}
                  <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                    {new Date(v.created_at).toLocaleDateString('pt-CV', { timeZone: 'Atlantic/Cape_Verde', day: '2-digit', month: 'short', year: 'numeric' })}
                  </Typography>
                </CardContent>
                <CardActions sx={{ pt: 0 }}>
                  {v.video_url && (
                    <Tooltip title="Ver vídeo">
                      <IconButton size="small" component="a" href={v.video_url} target="_blank" rel="noopener">
                        <OpenInNewIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {(v.status === 'pending' || v.status === 'processing') && (
                    <Tooltip title="Actualizar status">
                      <IconButton size="small" onClick={() => handleRefresh(v)}>
                        <RefreshIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Tooltip title="Remover">
                    <IconButton size="small" color="error" onClick={() => handleDelete(v)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Dialog: Gerar Vídeo */}
      <Dialog open={generateOpen} onClose={() => setGenerateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Gerar Vídeo com Higgsfield AI</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Prompt (descrição do vídeo)"
              multiline
              rows={4}
              value={form.prompt}
              onChange={e => setForm(f => ({ ...f, prompt: e.target.value }))}
              fullWidth
              required
              placeholder="Ex: Pessoa a pagar com o telemóvel numa loja em Cabo Verde, luz natural, ambiente moderno..."
              helperText="Descreva detalhadamente a cena que quer gerar"
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Estilo</InputLabel>
                  <Select
                    label="Estilo"
                    value={form.style}
                    onChange={e => setForm(f => ({ ...f, style: e.target.value }))}
                  >
                    {STYLES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Formato</InputLabel>
                  <Select
                    label="Formato"
                    value={form.aspect_ratio}
                    onChange={e => setForm(f => ({ ...f, aspect_ratio: e.target.value as any }))}
                  >
                    {ASPECT_RATIOS.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            <TextField
              label="Duração (segundos)"
              type="number"
              size="small"
              value={form.duration}
              onChange={e => setForm(f => ({ ...f, duration: Number(e.target.value) }))}
              inputProps={{ min: 2, max: 30 }}
              fullWidth
            />
            <Alert severity="info">
              A geração de vídeo pode demorar alguns minutos. O status é actualizado automaticamente.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateOpen(false)}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={saving || !form.prompt.trim()}
            sx={{ bgcolor: '#7C3AED', '&:hover': { bgcolor: '#6D28D9' } }}
          >
            {saving ? <CircularProgress size={18} /> : 'Gerar Vídeo'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
